import React from 'react';
import { Icon } from '@iconify/react';
import { formatKeyForDisplay } from '../utils/commonutils';
import {
  CLAIM_INTAKE_LABOR_FIELD_PRIORITY,
  CLAIM_INTAKE_PART_FIELD_PRIORITY,
  CLAIM_INTAKE_SECTION_CLASS,
  formatClaimIntakeFieldValue,
  orderClaimIntakeRowKeys,
} from './claimIntakeActionOutputFormatters';

const LABEL_CLASS = 'renderer-field-name text-[13px] leading-[18px] shrink-0';
const VALUE_CLASS = 'renderer-field-value text-[13px] leading-[18px]';
const INLINE_ROW_CLASS = 'flex justify-between items-start gap-2 text-sm';
const STACKED_ROW_CLASS = 'flex flex-col gap-1 text-sm';

export type ClaimIntakeKeyValueRowProps = {
  label: string;
  value: unknown;
  fieldKey?: string;
  stacked?: boolean;
};

export const ClaimIntakeKeyValueRow: React.FC<ClaimIntakeKeyValueRowProps> = ({
  label,
  value,
  fieldKey = label,
  stacked = false,
}) => {
  const strVal = formatClaimIntakeFieldValue(fieldKey, value);
  const useInline = !stacked && strVal.length < 60 && !strVal.includes('\n');
  const rowClass = useInline ? INLINE_ROW_CLASS : STACKED_ROW_CLASS;
  return (
    <div className={rowClass}>
      <span className={LABEL_CLASS}>{label}:</span>
      {useInline ? (
        <span className={`${VALUE_CLASS} text-right`}>{strVal}</span>
      ) : (
        <div className={`${VALUE_CLASS} whitespace-pre-line`}>{strVal}</div>
      )}
    </div>
  );
};

export type ClaimIntakeThreeCsFieldRowProps = {
  label: string;
  value: unknown;
  fieldKey?: string;
};

/** 3Cs fields: prominent heading, subdued body text (inverse of default key-value rows). */
export const ClaimIntakeThreeCsFieldRow: React.FC<ClaimIntakeThreeCsFieldRowProps> = ({
  label,
  value,
  fieldKey = label,
}) => {
  const strVal = formatClaimIntakeFieldValue(fieldKey, value);
  return (
    <div className="mb-2 last:mb-0">
      <div className="text-sm font-semibold text-foreground mb-1">{label}</div>
      <div className="text-[13px] text-default-500 whitespace-pre-line leading-relaxed">{strVal}</div>
    </div>
  );
};

export type ClaimIntakeSectionBlockProps = {
  title: string;
  children: React.ReactNode;
  icon?: string;
};

export const ClaimIntakeSectionBlock: React.FC<ClaimIntakeSectionBlockProps> = ({
  title,
  children,
  icon,
}) => (
  <div className={`rounded-lg border py-2 pr-2 pl-1 mb-2 last:mb-0 ${CLAIM_INTAKE_SECTION_CLASS}`}>
    <div className="flex items-center gap-2 mb-2">
      {icon ? <Icon icon={icon} className="w-4 h-4 text-default-500 shrink-0" /> : null}
      <div className="text-sm text-foreground capitalize">{title}</div>
    </div>
    <div className="flex flex-col gap-1">{children}</div>
  </div>
);

export type ClaimIntakeLineItemVariant = 'labor' | 'parts' | 'default';

const LINE_ITEM_CARD_CLASS = 'rounded-md border border-default-200 bg-content1 p-2 pl-1 mb-2 last:mb-0';

const LINE_ITEM_FIELD_PRIORITY: Record<ClaimIntakeLineItemVariant, string[]> = {
  labor: CLAIM_INTAKE_LABOR_FIELD_PRIORITY,
  parts: CLAIM_INTAKE_PART_FIELD_PRIORITY,
  default: [],
};

const LINE_ITEM_ICON: Record<ClaimIntakeLineItemVariant, string> = {
  labor: 'mdi:wrench-outline',
  parts: 'mdi:cog-outline',
  default: 'mdi:format-list-bulleted',
};

export type ClaimIntakeLineItemCardProps = {
  title: string;
  row: Record<string, unknown>;
  fieldPriority: string[];
};

export const ClaimIntakeLineItemCard: React.FC<ClaimIntakeLineItemCardProps> = ({
  title,
  row,
  fieldPriority,
}) => {
  const keys = orderClaimIntakeRowKeys(row, fieldPriority);
  if (!keys.length) return null;

  return (
    <div className={LINE_ITEM_CARD_CLASS}>
      <div className="text-xs font-semibold text-default-700 mb-1.5 pb-1 border-b border-default-200">{title}</div>
      <div className="flex flex-col gap-1">
        {keys.map((key) => (
          <ClaimIntakeKeyValueRow
            key={key}
            label={formatKeyForDisplay(key)}
            value={row[key]}
            fieldKey={key}
            stacked
          />
        ))}
      </div>
    </div>
  );
};

export type ClaimIntakeCollapsibleBlockProps = {
  id?: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  isOpen?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  summaryClassName?: string;
  contentClassName?: string;
};

/** Native collapsible — avoids NextUI Accordion collection crashes in the drawer. */
export const ClaimIntakeCollapsibleBlock: React.FC<ClaimIntakeCollapsibleBlockProps> = ({
  id,
  title,
  subtitle,
  children,
  isOpen,
  defaultOpen = false,
  onOpenChange,
  className = 'group rounded-md border border-default-200 bg-background/80 mb-2 last:mb-0',
  summaryClassName = 'flex cursor-pointer list-none items-center gap-2 px-2.5 py-2 text-sm [&::-webkit-details-marker]:hidden',
  contentClassName = 'border-t border-default-200 pl-1 pr-2 py-2',
}) => {
  const isControlled = isOpen !== undefined;
  const open = isControlled ? isOpen : defaultOpen;

  return (
    <details id={id} open={open || undefined} className={className}>
      <summary
        className={summaryClassName}
        onClick={(event) => {
          if (!isControlled) return;
          event.preventDefault();
          onOpenChange?.(!isOpen);
        }}
      >
        <Icon
          icon="mdi:chevron-right"
          className="w-4 h-4 shrink-0 text-default-500 transition-transform group-open:rotate-90"
        />
        <div className="min-w-0 flex-1">{title}</div>
        {subtitle ? <div className="min-w-0 shrink text-xs text-default-500 truncate">{subtitle}</div> : null}
      </summary>
      <div className={contentClassName}>{children}</div>
    </details>
  );
};

export type ClaimIntakeLineItemsListProps = {
  title: string;
  rows: Record<string, unknown>[];
  variant: ClaimIntakeLineItemVariant;
};

export const ClaimIntakeLineItemsList: React.FC<ClaimIntakeLineItemsListProps> = ({ title, rows, variant }) => {
  if (!rows.length) {
    return <div className="text-sm text-default-500">--</div>;
  }

  const fieldPriority = LINE_ITEM_FIELD_PRIORITY[variant];

  return (
    <ClaimIntakeSectionBlock title={`${title} (${rows.length})`} icon={LINE_ITEM_ICON[variant]}>
      {rows.map((row, idx) => (
        <ClaimIntakeLineItemCard
          key={`${variant}-line-${idx}`}
          title={`Line ${idx + 1}`}
          row={row}
          fieldPriority={fieldPriority}
        />
      ))}
    </ClaimIntakeSectionBlock>
  );
};
